import moment from "moment";

import { MILESTONE_KEY } from "../constants/milestone";
import {
    SCOPE_EMPTY,
    buildGutterScope,
    buildNailingVentilationScope,
    formatIceWaterShieldDisplay,
    formatPitchDisplay,
    formatRoofAreaDisplay,
    formatTearOffLayerDisplay,
    formatUnderlaymentDisplay,
    getPitchRows,
    getScopeDisplay,
    resolveNailSizesFromCart,
} from "./contractScopeFormatters";

const SPECIAL_PROJECT_NOTES_ATTRIBUTE = "Special Project Notes";

export interface ContractPricingApiResult {
    RoofPriceFormatted: string;
    GutterPriceFormatted: string;
    TotalPriceFormatted: string;
    PaymentSchedule: string;
    UpgradeNotes: string;
    UpgradeAmount: string;
    Breakdown?: Array<{
        label: string;
        amount: number;
        amountFormatted: string;
        isTotal?: boolean;
    }>;
}

function normalizeAttributesList(raw: unknown): any[] {
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object" && Array.isArray((raw as any).Data)) {
        return (raw as any).Data;
    }
    return [];
}

function formatContractDate(value?: string | Date | null): string {
    if (!value) return SCOPE_EMPTY;
    return moment(value).format("MMMM DD, YYYY");
}

function resolveEstimatedStartDate(project?: {
    StartDate?: string | Date | null;
    JobStartDate?: string | Date | null;
    ProjectTimelines?: Array<{
        DateType?: string;
        TargetDate?: string | Date | null;
        ActualDate?: string | Date | null;
    }>;
}): string {
    const jobStart = project?.ProjectTimelines?.find(
        (timeline) => timeline.DateType === MILESTONE_KEY.JOB_START,
    );
    const rawDate =
        jobStart?.TargetDate ||
        jobStart?.ActualDate ||
        project?.JobStartDate ||
        project?.StartDate;
    return rawDate ? formatContractDate(rawDate) : SCOPE_EMPTY;
}

function buildProjectNotes(attributes: any[]): string {
    return getScopeDisplay(attributes, SPECIAL_PROJECT_NOTES_ATTRIBUTE);
}

function projectField(value: unknown): string {
    if (value === null || value === undefined || String(value).trim() === "") {
        return SCOPE_EMPTY;
    }
    return String(value);
}

export interface BuildContractPreviewArgs {
    project: any;
    attributes: any[];
    contractPricing: ContractPricingApiResult;
    cartItems?: any[];
}

export function buildContractPreviewData({
    project,
    attributes,
    contractPricing,
    cartItems = [],
}: BuildContractPreviewArgs) {
    const safeAttributes = normalizeAttributesList(attributes);
    const nailSizes = resolveNailSizesFromCart(cartItems);

    const dateStr = moment().format("MMMM DD, YYYY");
    const squares = formatRoofAreaDisplay(safeAttributes);

    return {
        Header: {
            ReferenceNumber: String(project.Id || "0000").padStart(6, "0"),
            Date: dateStr,
            Status: "Preview",
        },
        OwnerInfo: {
            Name: projectField(project.CustomerName),
            SalesRep: projectField(project.SalesmanName),
            Date: dateStr,
            Address: projectField(project.CustomerStreet),
            City: projectField(project.CustomerCity),
            State: projectField(project.CustomerState),
            Zip:
                project.CustomerZipCode || project.CustomerCountry
                    ? `${project.CustomerZipCode || ""} · ${project.CustomerCountry || ""}`.trim()
                    : SCOPE_EMPTY,
            Phone: projectField(project.CustomerPhoneNumber),
            Email: projectField(project.CustomerEmail),
            InsuranceCompany: projectField(project.InsuranceProvider),
            ClaimNumber: projectField(project.InsuranceClaimNumber),
        },
        RoofingScope: {
            Material: getScopeDisplay(safeAttributes, "ShingleType"),
            Color: getScopeDisplay(safeAttributes, "RoofColor"),
            Pitch: formatPitchDisplay(safeAttributes),
            PitchRows: getPitchRows(safeAttributes),
            Layers: formatTearOffLayerDisplay(safeAttributes),
            Squares: squares,
            Underlayment: formatUnderlaymentDisplay(safeAttributes),
            IceWaterShield: formatIceWaterShieldDisplay(safeAttributes),
            StarterStrip: getScopeDisplay(safeAttributes, "Starter"),
            EaveDripEdge: getScopeDisplay(safeAttributes, "EaveDripEdge"),
            RakeDripEdge: getScopeDisplay(safeAttributes, "RakeDripEdge"),
            ValleyType: getScopeDisplay(safeAttributes, "ValleyType"),
            HipRidgeCap: getScopeDisplay(safeAttributes, "RidgeCapProfile"),
        },
        NailingVentilation: {
            FieldNailSize: nailSizes.FieldNailSize,
            FieldNailsPerShingle: nailSizes.FieldNailQty,
            RidgeNailSize: nailSizes.RidgeNailSize,
            RidgeNailsPerCap: nailSizes.RidgeNailQty,
            ...buildNailingVentilationScope(safeAttributes),
        },
        Notes: buildProjectNotes(safeAttributes),
        GutterScope: buildGutterScope(safeAttributes),
        Pricing: {
            RoofPrice: contractPricing.RoofPriceFormatted,
            GutterPrice: contractPricing.GutterPriceFormatted,
            TotalPrice: contractPricing.TotalPriceFormatted,
            PaymentSchedule: contractPricing.PaymentSchedule || SCOPE_EMPTY,
            EstimatedStartDate: resolveEstimatedStartDate(project),
            UpgradeNotes: contractPricing.UpgradeNotes || "",
            UpgradeAmount: contractPricing.UpgradeAmount || "",
            Breakdown: contractPricing.Breakdown,
        },
    };
}
